import React, { useRef, useLayoutEffect, useCallback, memo } from 'react';
import styled from 'styled-components';
import { handleCommaEntered, handleInvalidCharacterAfterZero, isCommaEntered, isFractionTooLong, isInvalidCharacterAfterZero, isMultipleCommas, ONLY_DIGITS_COMMA_OR_DOT_REGEXP, shouldAutoPrefixZero, } from './validationFunctions';
const Input = styled.input `
  border: none;
  width: 100%;
  flex: 1;
  font-size: 32px;
  line-height: 36px;
  background-color: ${(props) => props.theme.colors.background};
  color: ${(props) => props.error ? props.theme.colors.warning : props.theme.colors.textBodyMain};

  &:focus-visible {
    outline: none;
  }
`;
export const NumberInput = memo(({ value, onChange, precision, dataTestId, }) => {
    const inputRef = useRef(null);
    const cursorPositionRef = useRef(0);
    const previousValueRef = useRef(value);
    useLayoutEffect(() => {
        const input = inputRef.current;
        const isInputFocused = input && document.activeElement === input;
        const valueChanged = previousValueRef.current !== value;
        if (isInputFocused && valueChanged && cursorPositionRef.current > 0) {
            const maxPosition = value.length;
            const targetPosition = Math.min(cursorPositionRef.current, maxPosition);
            const currentPosition = input.selectionStart || 0;
            if (currentPosition !== targetPosition) {
                input.setSelectionRange(targetPosition, targetPosition);
            }
        }
        previousValueRef.current = value;
    }, [value]);
    const handleInputChange = useCallback((e) => {
        let { value } = e.target;
        if (e.target.selectionStart) {
            cursorPositionRef.current = e.target.selectionStart;
        }
        if (isInvalidCharacterAfterZero(value)) {
            value = handleInvalidCharacterAfterZero(value) || '';
            // Allow deletion even if value becomes '0'
            // if (value === '0') {
            //   return;
            // }
        }
        if (isCommaEntered(value)) {
            value = handleCommaEntered(value);
        }
        if (shouldAutoPrefixZero(value)) {
            value = '0' + value;
        }
        // now we handle the checks during onChange
        if (ONLY_DIGITS_COMMA_OR_DOT_REGEXP.test(value) &&
            !isMultipleCommas(value) &&
            !(precision && isFractionTooLong(value, precision)) &&
            !(value.includes(',') && value.includes('.'))) {
            onChange(value);
        }
    }, [onChange, precision]);
    // value validation on insertion
    const onPaste = (e) => {
        e.preventDefault();
        const pasteText = (e.clipboardData ||
            window.clipboardData).getData('text');
        const cleanedText = pasteText.replace(/[^0-9,.]/g, '');
        let finalText = cleanedText;
        // Check if there is a decimal point or comma in the pasted text
        const parts = cleanedText.split(/[,.]/);
        if (parts.length > 1 && precision) {
            const integerPart = parts[0];
            let fractionPart = parts[1];
            if (fractionPart.length > precision) {
                fractionPart = fractionPart.slice(0, precision);
            }
            finalText = `${integerPart}.${fractionPart}`;
        }
        document.execCommand('insertText', false, finalText);
    };
    return (<Input ref={inputRef} onPaste={onPaste} data-test-id={dataTestId} value={value} type="text" inputMode="decimal" onChange={handleInputChange}/>);
});
